// apps/admin/src/features/billing/RefundTaskCompleteDialog.tsx
//
// Phase 8.2 Wave 3 Task 20 — Complete Refund Task dialog (A6 queue).
//
// vg-binding: PLAN:task-20
// vg-binding: P8.D-485
// vg-binding: P8.D-494
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:idempotency-key
//
// Contract refs:
//   PUT /api/v1/admin/refund-tasks/:id/complete (API-CONTRACTS slice).
//
// Behaviour:
//   1. Bank reference REQUIRED (min 3, max 100 per zod).
//   2. Notes optional (max 500).
//   3. Bank-statement file upload optional (PDF / JPEG / PNG, ≤5MB) —
//      uploaded via /api/v1/files/upload first, then the returned URL is
//      sent as `proof_file_url` in the complete body (the name the route
//      schema, the body parser and the RefundTask model all use; see
//      dbg-20260725-refund-complete-400 at the submit call).
//   4. 4-eyes:
//        - Same-user (`self_segregation_blocked=true` from server, or
//          `task.created_by === current_user.id` fallback) blocks the
//          submit button unless the admin override checkbox is ticked.
//        - Override requires written reason (min 20 chars per zod refine).
//        - BE logs audit_log entity `override_segregation` per Block 2.
//   5. expected_version sent from row.version for optimistic locking.
//
// Test IDs (task-20):
//   refund-task-complete-dialog
//   refund-task-bank-ref-input
//   refund-task-override-segregation-checkbox
//   refund-task-override-reason-input
//   refund-task-submit-btn

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';

import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useAuth,
  useToast,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import { uploadFileAbsoluteUrl } from '../../lib/uploadFile.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Constants — file-upload contract (mirror BE /api/v1/files/upload guard)
// ---------------------------------------------------------------------------

const MAX_PROOF_BYTES = 5 * 1024 * 1024;
const ACCEPTED_PROOF_MIMES: ReadonlySet<string> = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
]);
const ACCEPTED_PROOF_EXTS = ['.pdf', '.jpg', '.jpeg', '.png'];

const BANK_REF_MIN = 3;
const BANK_REF_MAX = 100;
const NOTES_MAX = 500;
const OVERRIDE_REASON_MIN = 20;
const OVERRIDE_REASON_MAX = 500;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface RefundTaskCompleteSummary {
  id: string;
  refund_task_number: string;
  merchant_name: string;
  amount_cents: number;
  bank_account_info: string;
  created_by: string;
  created_by_name: string;
  /** Server-side computed 4-eyes flag (overrides client check). */
  self_segregation_blocked: boolean;
  /** Optimistic-lock version supplied by GET list. */
  version: number;
}

export interface RefundTaskCompleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: RefundTaskCompleteSummary | null;
  onSubmitted?: () => void;
}

interface ApiErrorEnvelope {
  ok?: false;
  error?: {
    code?: string;
    message?: string;
    user_message?: string;
    field_errors?: Record<string, string[]>;
  };
  success?: false;
}

type LooseClient = {
  PUT: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function newIdempotencyKey(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `refund-complete-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
}

function formatUsdCents(cents: number): string {
  const dollars = (cents ?? 0) / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

function extractApiMessage(
  payload: unknown,
  fallback: string,
): string {
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    if (env.error?.user_message) return env.error.user_message;
    if (env.error?.message) return env.error.message;
  }
  return fallback;
}

/**
 * Server error codes meaning "this row is gone or already handled" — the queue
 * on screen is stale and no amount of retrying will help.
 *
 * dbg-20260725-refund-complete-400 (follow-up): reproduced by deleting a task
 * behind an open queue, then pressing Hoàn tất on the row still rendered:
 *   PUT …/complete → 404 REFUND_TASK_NOT_FOUND
 * The dialog stayed open, the dead row stayed in the table and the accountant
 * only saw "Hoàn tất hoàn tiền thất bại" — nothing telling them the row no
 * longer exists, so the natural reaction is to keep pressing. Same trap for
 * REFUND_TASK_NOT_PENDING (409) when a colleague completes it first.
 *
 * Matched by CODE, not by HTTP status, because 409 is overloaded:
 * BANK_REFERENCE_DUPLICATE is also a 409 but IS user-correctable (type another
 * wire reference), so it must keep the dialog open.
 *
 * Mirrors RejectPayoutModal, which already closes + refetches on a stale 409.
 */
const STALE_TASK_CODES: ReadonlySet<string> = new Set([
  'REFUND_TASK_NOT_FOUND',
  'REFUND_TASK_NOT_PENDING',
]);

function extractApiCode(payload: unknown): string | undefined {
  if (payload && typeof payload === 'object') {
    return (payload as ApiErrorEnvelope).error?.code;
  }
  return undefined;
}

function extractFieldErrors(
  payload: unknown,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    const fe = env.error?.field_errors;
    if (fe && typeof fe === 'object') {
      for (const [k, v] of Object.entries(fe)) {
        if (Array.isArray(v) && v.length > 0) {
          out[k] = v[0] ?? '';
        }
      }
    }
  }
  return out;
}

/**
 * Upload optional bank-statement proof via the unified upload client
 * (`POST /api/v1/files`, scope `topup-receipt`). Returns the canonical
 * proof URL on success, `null` on failure (caller surfaces inline error).
 * dbg-20260622-unified-upload — replaced hand-rolled fetch + `file_id`
 * parsing with the shared `uploadFileAbsoluteUrl`.
 */
async function uploadProofFile(file: File): Promise<string | null> {
  try {
    return await uploadFileAbsoluteUrl({ file, scope: 'topup-receipt' });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RefundTaskCompleteDialog({
  open,
  onOpenChange,
  task,
  onSubmitted,
}: RefundTaskCompleteDialogProps): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const currentUser = useAuth((s) => s.user);
  const isAdmin =
    currentUser?.primaryRole === 'admin' ||
    currentUser?.primaryRole === 'superadmin';

  const [bankReference, setBankReference] = useState('');
  const [notes, setNotes] = useState('');
  const [overrideSegregation, setOverrideSegregation] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofError, setProofError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset on open ↔ close.
  useEffect(() => {
    if (!open) {
      setBankReference('');
      setNotes('');
      setOverrideSegregation(false);
      setOverrideReason('');
      setProofFile(null);
      setProofError(null);
      setFieldErrors({});
      setSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  if (!task) return null;

  // Server-side flag is canonical, fall back to client check.
  const sameUserBlocked =
    task.self_segregation_blocked ||
    (currentUser?.id != null && currentUser.id === task.created_by);

  function handlePickFile(file: File | null): void {
    setProofError(null);
    if (!file) {
      setProofFile(null);
      return;
    }
    const lowerName = file.name.toLowerCase();
    const looksAcceptedByExt = ACCEPTED_PROOF_EXTS.some((ext) =>
      lowerName.endsWith(ext),
    );
    if (!ACCEPTED_PROOF_MIMES.has(file.type) && !looksAcceptedByExt) {
      setProofError(t('refund_task.complete.proofInvalidFormat'));
      setProofFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_PROOF_BYTES) {
      setProofError(
        t('refund_task.complete.proofTooLarge', {
          size: (file.size / 1024 / 1024).toFixed(2),
        }),
      );
      setProofFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setProofFile(file);
  }

  function handleClearFile(): void {
    setProofFile(null);
    setProofError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function submitComplete(): Promise<void> {
    if (!task) return;
    // Local validation.
    const localErrors: Record<string, string> = {};
    const ref = bankReference.trim();
    if (ref.length < BANK_REF_MIN) {
      localErrors.bank_reference = t('refund_task.complete.bankRefMin', { min: BANK_REF_MIN });
    } else if (ref.length > BANK_REF_MAX) {
      localErrors.bank_reference = t('refund_task.complete.bankRefMax', { max: BANK_REF_MAX });
    }
    if (overrideSegregation) {
      const reason = overrideReason.trim();
      if (reason.length < OVERRIDE_REASON_MIN) {
        localErrors.override_reason = t('refund_task.complete.overrideReasonMin', { min: OVERRIDE_REASON_MIN });
      } else if (reason.length > OVERRIDE_REASON_MAX) {
        localErrors.override_reason = t('refund_task.complete.overrideReasonMax', { max: OVERRIDE_REASON_MAX });
      }
    }
    if (sameUserBlocked && !overrideSegregation) {
      localErrors.override_segregation = isAdmin
        ? t('refund_task.complete.sameUserAdmin')
        : t('refund_task.complete.sameUserNonAdmin');
    }
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }

    setSubmitting(true);
    setFieldErrors({});

    // Upload proof file first if present.
    let proofUrl: string | null = null;
    if (proofFile) {
      proofUrl = await uploadProofFile(proofFile);
      if (!proofUrl) {
        setSubmitting(false);
        setProofError(t('refund_task.complete.proofUploadFailed'));
        return;
      }
    }

    try {
      const client = apiClient() as unknown as LooseClient;
      const body: Record<string, unknown> = {
        bank_reference: ref,
        expected_version: task.version,
        override_segregation: overrideSegregation,
      };
      const trimmedNotes = notes.trim();
      if (trimmedNotes) body.notes = trimmedNotes;
      // dbg-20260725-refund-complete-400: the field is `proof_file_url` on the
      // wire, NOT `bank_transfer_proof_url`. The route's `completeBody` schema
      // is `additionalProperties: false` and lists `proof_file_url`, the parse
      // step reads `body['proof_file_url']`, and the model persists
      // `IRefundTask.proof_file_url`. Sending the other name made the request
      // fail validation before reaching the handler:
      //   PUT /api/v1/admin/refund-tasks/:id/complete
      //   → 400 FST_ERR_VALIDATION "data must NOT have additional properties"
      // The bug only fires when a bank statement is ATTACHED — without a file
      // this key is absent and the same request succeeds, which is why the
      // no-attachment path looked healthy. Every attempt still uploaded the
      // file first, so each retry left an orphaned upload behind.
      if (proofUrl) body.proof_file_url = proofUrl;
      if (overrideSegregation) body.override_reason = overrideReason.trim();

      const { data, error, response } = await client.PUT(
        `/api/v1/admin/refund-tasks/${encodeURIComponent(task.id)}/complete`,
        {
          body,
          headers: {
            'Idempotency-Key': newIdempotencyKey(),
            'Content-Type': 'application/json',
          },
        },
      );
      if (error || !response.ok) {
        // Row is gone / already handled → the queue is stale. Close and refetch
        // so the dead row disappears instead of inviting another retry.
        if (STALE_TASK_CODES.has(extractApiCode(error) ?? '')) {
          toast({
            title: t('refund_task.complete.toastStaleTitle'),
            description: t('refund_task.complete.toastStaleDesc'),
            variant: 'warning',
          });
          onOpenChange(false);
          onSubmitted?.();
          return;
        }
        const fe = extractFieldErrors(error);
        if (Object.keys(fe).length > 0) setFieldErrors(fe);
        toast({
          title: t('refund_task.complete.toastFailed'),
          description: extractApiMessage(error, t('refund_task.errors.generic')),
          variant: 'error',
        });
        return;
      }
      void data;
      toast({
        title: t('refund_task.complete.toastSuccess'),
        description: t('refund_task.complete.toastSuccessDesc', {
          taskNumber: task.refund_task_number,
        }),
        variant: 'success',
      });
      onOpenChange(false);
      onSubmitted?.();
    } catch (err) {
      toast({
        title: t('refund_task.complete.networkError'),
        description:
          err instanceof Error && err.message
            ? err.message
            : t('refund_task.complete.networkErrorDesc'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        onOpenChange(next);
      }}
      className="max-w-lg"
    >
      <div
        data-testid="refund-task-complete-dialog"
        className="flex flex-col gap-4"
      >
        <DialogHeader>
          <DialogTitle>
            {t('refund_task.complete.title', { taskNumber: task.refund_task_number })}
          </DialogTitle>
        </DialogHeader>

        <form
          data-testid="refund-task-complete-form"
          // dbg-20260725-uat-fin-2629-2632 (#2629 bước 5): the bank-ref input
          // carries `required` + `minLength`, so once the submit button became
          // clickable the browser's NATIVE constraint bubble fired first
          // ("Please fill in this field." — rendered in the BROWSER's locale, not
          // the app's) and submitComplete() never ran, so our translated
          // messages still never appeared. noValidate hands validation to
          // submitComplete(), which renders the i18n text inline. The HTML
          // attributes stay for a11y / semantics.
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!submitting) void submitComplete();
          }}
          className="flex flex-col gap-4"
        >
          {/* Task summary */}
          <div className="rounded-md border border-pw-border bg-pw-surface/50 px-3 py-2 text-xs text-pw-text-muted">
            <div>
              <span className="font-medium text-pw-text">{t('refund_task.complete.summaryMerchant')}</span>{' '}
              {task.merchant_name}
            </div>
            <div>
              <span className="font-medium text-pw-text">{t('refund_task.complete.summaryAmount')}</span>{' '}
              {formatUsdCents(task.amount_cents)}
            </div>
            <div>
              <span className="font-medium text-pw-text">{t('refund_task.complete.summaryBankAccount')}</span>{' '}
              {task.bank_account_info}
            </div>
            <div>
              <span className="font-medium text-pw-text">{t('refund_task.complete.summaryCreatedBy')}</span>{' '}
              {task.created_by_name || task.created_by}
            </div>
          </div>

          {/* 4-eyes warning banner */}
          {sameUserBlocked ? (
            <div
              role="alert"
              data-testid="refund-task-segregation-warning"
              className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
            >
              <p className="font-medium">
                {t('refund_task.complete.bannerTitle')}
              </p>
              <p className="mt-1">
                {isAdmin
                  ? t('refund_task.complete.bannerAdmin')
                  : t('refund_task.complete.bannerNonAdmin')}
              </p>
            </div>
          ) : null}

          {/* Bank reference */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="rt-bank-ref"
              className="text-sm font-medium text-pw-text"
            >
              {t('refund_task.complete.bankRefLabel')} <span className="text-red-600 dark:text-red-300">*</span>
            </label>
            <Input
              id="rt-bank-ref"
              data-testid="refund-task-bank-ref-input"
              value={bankReference}
              onChange={(e) => setBankReference(e.target.value)}
              placeholder={t('refund_task.complete.bankRefPlaceholder')}
              maxLength={BANK_REF_MAX}
              minLength={BANK_REF_MIN}
              aria-invalid={Boolean(fieldErrors.bank_reference)}
              disabled={submitting}
              required
            />
            {fieldErrors.bank_reference ? (
              <p className="text-xs text-red-600 dark:text-red-300" role="alert">
                {fieldErrors.bank_reference}
              </p>
            ) : (
              <p className="text-xs text-pw-text-muted">
                {t('refund_task.complete.bankRefHelp')}
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="rt-notes"
              className="text-sm font-medium text-pw-text"
            >
              {t('refund_task.complete.notesLabel')}
            </label>
            <textarea
              id="rt-notes"
              data-testid="refund-task-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('refund_task.complete.notesPlaceholder')}
              maxLength={NOTES_MAX}
              rows={3}
              className="rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm text-pw-text outline-none focus:border-pw-primary focus:ring-1 focus:ring-pw-primary"
              aria-invalid={Boolean(fieldErrors.notes)}
              disabled={submitting}
            />
            {fieldErrors.notes ? (
              <p className="text-xs text-red-600 dark:text-red-300">{fieldErrors.notes}</p>
            ) : null}
          </div>

          {/* Optional bank-statement file upload */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="rt-proof-file"
              className="text-sm font-medium text-pw-text"
            >
              {t('refund_task.complete.proofLabel')}
            </label>
            <input
              id="rt-proof-file"
              ref={fileInputRef}
              data-testid="refund-task-proof-file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              disabled={submitting}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                handlePickFile(f);
              }}
              className="block w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-pw-primary/10 file:px-3 file:py-1 file:text-sm file:font-medium file:text-pw-primary hover:file:bg-pw-primary/20"
            />
            {proofFile ? (
              <div
                className="mt-1 flex items-center justify-between rounded-md border border-pw-border bg-pw-surface px-3 py-2 text-xs"
                data-testid="refund-task-proof-file-selected"
              >
                <span className="truncate text-pw-text">
                  {proofFile.name} ·{' '}
                  {(proofFile.size / 1024).toFixed(1)} KB
                </span>
                <button
                  type="button"
                  className="ml-3 text-rose-600 dark:text-rose-300 hover:underline"
                  onClick={handleClearFile}
                  disabled={submitting}
                >
                  {t('refund_task.complete.proofRemove')}
                </button>
              </div>
            ) : (
              <p className="text-xs text-pw-text-muted">
                {t('refund_task.complete.proofHelp')}
              </p>
            )}
            {proofError ? (
              <p
                className="text-xs text-red-600 dark:text-red-300"
                role="alert"
                data-testid="refund-task-proof-file-error"
              >
                {proofError}
              </p>
            ) : null}
          </div>

          {/* Override segregation — only shown when same-user block triggers
              AND current user is admin. Non-admins cannot bypass. */}
          {sameUserBlocked && isAdmin ? (
            <div className="flex flex-col gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/50 p-3">
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-pw-text">
                <input
                  type="checkbox"
                  data-testid="refund-task-override-segregation-checkbox"
                  checked={overrideSegregation}
                  onChange={(e) => setOverrideSegregation(e.target.checked)}
                  disabled={submitting}
                  className="h-4 w-4 rounded border-pw-border accent-pw-primary"
                />
                {t('refund_task.complete.overrideCheckbox')}
              </label>
              {overrideSegregation ? (
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="rt-override-reason"
                    className="text-xs font-medium text-pw-text-muted"
                  >
                    {t('refund_task.complete.overrideReasonLabel')} <span className="text-red-600 dark:text-red-300">*</span>
                  </label>
                  <textarea
                    id="rt-override-reason"
                    data-testid="refund-task-override-reason-input"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder={t('refund_task.complete.overrideReasonPlaceholder')}
                    minLength={OVERRIDE_REASON_MIN}
                    maxLength={OVERRIDE_REASON_MAX}
                    rows={3}
                    className="rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm text-pw-text outline-none focus:border-pw-primary focus:ring-1 focus:ring-pw-primary"
                    aria-invalid={Boolean(fieldErrors.override_reason)}
                    disabled={submitting}
                    required
                  />
                  {fieldErrors.override_reason ? (
                    <p className="text-xs text-red-600 dark:text-red-300" role="alert">
                      {fieldErrors.override_reason}
                    </p>
                  ) : (
                    <p className="text-xs text-pw-text-muted">
                      {t('refund_task.complete.overrideReasonHelp', { min: OVERRIDE_REASON_MIN })}
                    </p>
                  )}
                </div>
              ) : null}
              {fieldErrors.override_segregation ? (
                <p className="text-xs text-red-600 dark:text-red-300" role="alert">
                  {fieldErrors.override_segregation}
                </p>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t('refund_task.complete.cancel')}
            </Button>
            <Button
              type="submit"
              variant="default"
              data-testid="refund-task-submit-btn"
              // dbg-20260725-uat-fin-2629-2632 (#2629, UAT-FIN-012 bước 5):
              // this used to also disable on `bankReference < BANK_REF_MIN` and
              // on the 4-eyes block. A disabled button explains NOTHING — the
              // accountant saw a red input outline, a dead button, and no
              // message at all (only the always-on help text). submitComplete()
              // already validates both conditions and renders the exact
              // messages (fieldErrors.bank_reference /
              // fieldErrors.override_segregation), so let the click through and
              // let validation speak. It still never submits: submitComplete()
              // returns early whenever localErrors is non-empty.
              disabled={submitting}
            >
              {submitting
                ? t('refund_task.complete.submitting')
                : t('refund_task.complete.submit')}
            </Button>
          </DialogFooter>
        </form>
      </div>
    </Dialog>
  );
}

export default RefundTaskCompleteDialog;
